import { useCallback, useRef, useState } from 'react';
import type { Worker as TesseractWorker } from 'tesseract.js';

import { cleanOCRText, isQualityOCRResult } from './ocrTextCleaning';

interface OCRResult {
  text: string;
  confidence: number;
}

interface UseOCRReturn {
  recognize: (imageUrl: string) => Promise<OCRResult | null>;
  isLoading: boolean;
  progress: number;
  error: string | null;
  terminate: () => Promise<void>;
}

const useOCR = (): UseOCRReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const workerRef = useRef<TesseractWorker | null>(null);

  const getWorker = useCallback(async (): Promise<TesseractWorker> => {
    if (workerRef.current) {
      return workerRef.current;
    }

    const { createWorker } = await import('tesseract.js');

    const worker = await createWorker('eng', 1, {
      logger: (m) => {
        if (m.status === 'recognizing text') {
          setProgress(Math.round(m.progress * 100));
        }
      },
    });

    workerRef.current = worker;
    return worker;
  }, []);

  const recognize = useCallback(
    async (imageUrl: string): Promise<OCRResult | null> => {
      setIsLoading(true);
      setProgress(0);
      setError(null);

      try {
        const worker = await getWorker();
        const {
          data: { text, confidence },
        } = await worker.recognize(imageUrl);

        const cleanedText = cleanOCRText(text);
        const isQuality = isQualityOCRResult(cleanedText, confidence);

        return { text: isQuality ? cleanedText : '', confidence };
      } catch {
        setError('Text extraction failed. Try again.');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [getWorker]
  );

  const terminate = useCallback(async () => {
    if (workerRef.current) {
      await workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  return { recognize, isLoading, progress, error, terminate };
};

export default useOCR;
